import type { ConnectionMetrics, ConnectionPath } from '../stats';

export const QUALITY_PROFILE_NAMES = [
  'ULTRA',
  'HIGH',
  'STANDARD',
  'MEDIUM',
  'LOW',
  'SURVIVAL',
] as const;

export type QualityProfileName = (typeof QUALITY_PROFILE_NAMES)[number];

export interface QualityProfile {
  readonly name: QualityProfileName;
  readonly maxWidth: number;
  readonly maxHeight: number;
  readonly maxBitrateBps: number;
  readonly minBitrateBps: number;
  readonly maxFramesPerSecond: number;
  readonly reducedFramesPerSecond: number;
  readonly upgradeAvailableBitrateBps: number;
}

export interface AdaptiveQualityConfig {
  readonly profiles: readonly QualityProfile[];
  readonly initialProfile: QualityProfileName;
  readonly movingAverageWindowSize: number;
  readonly minimumSamples: number;
  readonly degradeConsecutiveSamples: number;
  readonly severeDegradeConsecutiveSamples: number;
  readonly upgradeConsecutiveSamples: number;
  readonly degradeCooldownMs: number;
  readonly upgradeCooldownMs: number;
  readonly packetLossDegradePercentage: number;
  readonly packetLossSeverePercentage: number;
  readonly packetLossRecoveryPercentage: number;
  readonly rttDegradeMs: number;
  readonly rttSevereMs: number;
  readonly rttRecoveryMs: number;
  readonly bandwidthDegradeRatio: number;
  readonly bandwidthSevereRatio: number;
  readonly upgradeHeadroomRatio: number;
  readonly bitrateUtilizationRatio: number;
  readonly degradationBitrateReductionRatio: number;
  readonly minimumBitrateChangeRatio: number;
  readonly cpuLimitedSampleRatio: number;
  readonly bandwidthLimitedSampleRatio: number;
  readonly lanUpgradeThresholdMultiplier: number;
  readonly lanBitrateMultiplier: number;
  readonly turnUpgradeThresholdMultiplier: number;
  readonly turnBitrateMultiplier: number;
  readonly turnMaximumProfile: QualityProfileName;
  readonly unknownPathMaximumProfile: QualityProfileName;
}

export type QualityAction =
  | 'reduce-bitrate'
  | 'reduce-framerate'
  | 'reduce-resolution'
  | 'increase-bitrate'
  | 'increase-framerate'
  | 'increase-resolution'
  | 'path-policy';

export type QualityDecisionReason =
  'bandwidth' | 'packet-loss' | 'rtt' | 'cpu' | 'recovery' | 'path-policy';

export interface QualityTarget {
  readonly profile: QualityProfileName;
  readonly maxWidth: number;
  readonly maxHeight: number;
  readonly maxBitrateBps: number;
  readonly maxFramesPerSecond: number;
}

export interface QualityDecision extends QualityTarget {
  readonly action: QualityAction;
  readonly reason: QualityDecisionReason;
  readonly decidedAt: number;
}

export interface AveragedConnectionMetrics {
  readonly sampleCount: number;
  readonly availableOutgoingBitrateBps: number | null;
  readonly outboundBitrateBps: number | null;
  readonly packetLossPercentage: number | null;
  readonly rttMs: number | null;
  readonly cpuLimitedRatio: number;
  readonly bandwidthLimitedRatio: number;
  readonly connectionPath: ConnectionPath;
  readonly lanLike: boolean;
}

export interface QualityEvaluation {
  readonly metrics: AveragedConnectionMetrics;
  readonly target: QualityTarget;
  readonly decision: QualityDecision | null;
  readonly degradedSampleStreak: number;
  readonly healthySampleStreak: number;
}

export interface AdaptiveQualityEvents {
  readonly decision: { readonly decision: QualityDecision };
  readonly evaluation: { readonly evaluation: QualityEvaluation };
}

export interface SenderQualityApplyResult {
  readonly applied: boolean;
  readonly appliedFields: readonly ('bitrate' | 'framerate' | 'resolution')[];
  readonly scaleResolutionDownBy: number | null;
}

export class SenderQualityApplyError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'SenderQualityApplyError';
  }
}

export interface QualityMetricsWindow {
  push(metrics: ConnectionMetrics): void;
  clear(): void;
  values(): readonly ConnectionMetrics[];
}
